import { confirmDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { html, nothing } from 'lit';
import type { Abwesenheit } from '../../../db/abwesenheit.js';
import { DialogResult } from '../../../db/enums/misc-enums.js';
import { Role } from '../../../permissions/roles.js';
import type { Person } from '../../../users/entities/person.js';
import { capitalizeFirstLetter } from '../../client-utils.js';

export interface AbwesenheitDetailDialogProps {
  abwesenheit: Abwesenheit;
  /** whether the user may delete the absence */
  readonly?: boolean;
}

/**
 * Details of a single absence, opened from the calendar.
 *
 * `start` and `ende` are inclusive calendar days stored canonically (00:00:00.000Z, see
 * `@adornis/datetime/calendarDay.js`). They are therefore rendered UTC-pinned and compared as plain
 * instants against `todayAsCalendarDay()` - reading them in the viewer's zone would show the
 * previous day west of Greenwich, and comparing a day against `now` would make the delete button
 * disappear from 02:00 German time onwards for an absence starting today.
 */
const AbwesenheitDetailDialogContent = stateful(
  (
    props: AbwesenheitDetailDialogProps & {
      resolve: (result: DialogResult) => void;
      reject: () => void;
    },
  ) => {
    const user = useObservable(() => currentUser<Person>(), []);
    const { abwesenheit } = props;
    const start = abwesenheit.start ?? null;
    const ende = abwesenheit.ende ?? null;

    const mayAlwaysDelete = !!user?.roles.find(
      role =>
        role.name === Role.ADMIN ||
        role.name === Role.SCHULLEITER ||
        (role.name === Role.PSB && role.contexts.includes(GLOBAL_CONTEXT)),
    );
    const startsTodayOrLater = !!start && start.getTime() >= todayAsCalendarDay().getTime();
    const mayDelete = (!props.readonly && startsTodayOrLater) || mayAlwaysDelete;

    const remove = async () => {
      if (!(await confirmDialog('Antrag löschen?'))) return;
      props.resolve(DialogResult.DELETE);
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: String(clientTranslator.translate('_class', abwesenheit._class)),
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <div class="form-grid">
          ${FormGridEntry({
            label: 'Start',
            render: html`<p>${start ? formatCalendarDay(start, 'dd.MM.yyyy') : '---'}</p>`,
          })}
          ${FormGridEntry({
            label: 'Ende',
            render: html`<p>${ende ? formatCalendarDay(ende, 'dd.MM.yyyy') : '---'}</p>`,
          })}
          ${abwesenheit.ganztaegig ? FormGridEntry({ label: 'Ganztägig?', render: html`<p>Ja</p>` }) : nothing}
          ${
            abwesenheit.partOfTheDay && !abwesenheit.ganztaegig
              ? FormGridEntry({
                  label: 'Abwesenheitszeitraum',
                  render: html`<p>${capitalizeFirstLetter(abwesenheit.partOfTheDay)}</p>`,
                })
              : nothing
          }
          ${FormGridEntry({ label: 'Kommentar', render: html`<p>${abwesenheit.kommentar ?? '---'}</p>` })}
        </div>

        <div class="flex flex-row justify-between gap-2">
          <button class="btn btn-ghost" @click=${() => props.resolve(DialogResult.CANCEL)}>Schließen</button>
          ${
            mayDelete
              ? html`
                  <button class="btn btn-error btn-outline" @click=${remove}>
                    <span class="icon-[material-symbols--delete-outline] text-lg"></span>
                    Löschen
                  </button>
                `
              : nothing
          }
        </div>
      </div>
    `;
  },
);

/**
 * Opens the absence detail dialog.
 *
 * @returns how the dialog was closed - {@link DialogResult.DELETE} if the user confirmed deletion,
 *   {@link DialogResult.CANCEL} otherwise (including dismissal).
 */
export async function showAbwesenheitDetailDialog(props: AbwesenheitDetailDialogProps): Promise<DialogResult> {
  try {
    return await showDialog<DialogResult>({
      modalBoxClasses: 'max-w-2xl',
      content: (resolve, reject) => AbwesenheitDetailDialogContent({ ...props, resolve, reject }),
    });
  } catch {
    // dialog was dismissed
    return DialogResult.CANCEL;
  }
}
